import { useState } from 'react';
import { PictureOutlined } from '@ant-design/icons';
import { useIntl } from 'react-intl';
import { useAppSelector } from '@/AppRedux/store/hooks';
import HotelProfileStateHook from '@/hooks/useHotelProfileState';
import PhotoGalleryModal from '@/modules/booking/components/PhotoGalleryModal';
import useIsMobile from '@/hooks/useIsMobile';

export default function HotelPhotoGrid() {
  const intl = useIntl();
  const { dataSource } = HotelProfileStateHook();
  const galleryMode = useAppSelector((state) => state.hotelProfile.hotelSettings?.gallery_mode)
  const hotelCarousel: string[] = dataSource?.hotelCarousel ?? [];

  const isMobile = useIsMobile(768);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [galleryIndex, setGalleryIndex] = useState(0);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  const modeVisible = galleryMode === 'grid' || galleryMode === 'both'
  if (!modeVisible || !hotelCarousel || hotelCarousel.length === 0) return null;

  const openGallery = (index: number) => {
    setGalleryIndex(index);
    setGalleryOpen(true);
  };

  // Mobile shows 4 (1 main + 3 thumbs), desktop shows 5 (1 main + 4 thumbs)
  const maxVisible = isMobile ? 4 : 5;
  const visibleImages = hotelCarousel.slice(0, maxVisible);
  const hiddenCount = hotelCarousel.length - maxVisible;
  const [main, ...thumbs] = visibleImages;

  const CoverCell = ({
    src,
    index,
    style,
    showMore,
  }: {
    src: string;
    index: number;
    style?: React.CSSProperties;
    showMore?: boolean;
  }) => (
    <div
      onClick={() => openGallery(index)}
      onMouseEnter={() => setHoveredIndex(index)}
      onMouseLeave={() => setHoveredIndex(null)}
      style={{ position: 'relative', overflow: 'hidden', cursor: 'pointer', ...style }}
    >
      <img
        src={src}
        alt={`hotel-photo-${index + 1}`}
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          display: 'block',
          transition: 'transform 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
          transform: hoveredIndex === index && !showMore ? 'scale(1.06)' : 'scale(1)',
        }}
      />
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background: 'rgba(0,0,0,0.18)',
          opacity: hoveredIndex === index ? 1 : 0,
          transition: 'opacity 0.3s',
          pointerEvents: 'none',
        }}
      />
      {showMore && hiddenCount > 0 && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 8,
            color: '#fff',
            pointerEvents: 'none',
          }}
        >
          <PictureOutlined style={{ fontSize: 22 }} />
          <span style={{ fontSize: 15, fontWeight: 700 }}>+{hiddenCount} รูป</span>
        </div>
      )}
    </div>
  );

  const viewAllButton = hotelCarousel.length > 1 && (
    <button
      onClick={() => openGallery(0)}
      style={{
        position: 'absolute',
        bottom: 12,
        right: 12,
        zIndex: 2,
        background: 'rgba(255,255,255,0.96)',
        border: 'none',
        borderRadius: 8,
        padding: '6px 14px',
        cursor: 'pointer',
        fontSize: 12,
        fontWeight: 600,
        boxShadow: '0 2px 12px rgba(0,0,0,0.22)',
        backdropFilter: 'blur(6px)',
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        lineHeight: '1.5',
      }}
    >
      <PictureOutlined style={{ fontSize: 13 }} />
      {intl.formatMessage({ id: 'hotel.viewAllPhotos', defaultMessage: 'ดูรูปทั้งหมด' })} ({hotelCarousel.length})
    </button>
  );

  // ── Mobile layout ─────────────────────────────────────────
  // [ main (full width, 220px) ]
  // [ t0 (1/3) ] [ t1 (1/3) ] [ t2+N (1/3) ]  110px
  if (isMobile) {
    const mobileThumbCount = Math.min(thumbs.length, 3);
    return (
      <>
        <div style={{ borderRadius: 16, overflow: 'hidden', position: 'relative' }}>
          <div style={{ display: 'grid', gap: 3 }}>
            <CoverCell src={main} index={0} style={{ height: 220 }} />
            {thumbs.length > 0 && (
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: `repeat(${mobileThumbCount}, 1fr)`,
                  gap: 3,
                  height: 110,
                }}
              >
                {thumbs.slice(0, 3).map((src, i) => (
                  <CoverCell
                    key={i}
                    src={src}
                    index={i + 1}
                    showMore={i === mobileThumbCount - 1}
                  />
                ))}
              </div>
            )}
          </div>
          {viewAllButton}
        </div>
        <PhotoGalleryModal
          images={hotelCarousel}
          open={galleryOpen}
          initialIndex={galleryIndex}
          onClose={() => setGalleryOpen(false)}
        />
      </>
    );
  }

  // ── Desktop layout ────────────────────────────────────────
  // [ main (60%) ] [ t0 ] [ t1 ]   460px
  //               [ t2 ] [ t3+N ]
  const thumbCols = thumbs.length <= 2 ? 1 : 2;
  const thumbRows = thumbCols === 1 ? thumbs.length : 2;

  return (
    <>
      <div style={{ borderRadius: 16, overflow: 'hidden', position: 'relative' }}>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: thumbs.length > 0 ? '3fr 2fr' : '1fr',
            gap: 3,
            height: 460,
          }}
        >
          <CoverCell src={main} index={0} />
          {thumbs.length > 0 && (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: `repeat(${thumbCols}, 1fr)`,
                gridTemplateRows: `repeat(${thumbRows}, 1fr)`,
                gap: 3,
              }}
            >
              {thumbs.map((src, i) => (
                <CoverCell
                  key={i}
                  src={src}
                  index={i + 1}
                  showMore={i === thumbs.length - 1}
                  style={
                    thumbs.length === 3 && i === 2
                      ? { gridColumn: '1 / span 2' }
                      : undefined
                  }
                />
              ))}
            </div>
          )}
        </div>
        {viewAllButton}
      </div>
      <PhotoGalleryModal
        images={hotelCarousel}
        open={galleryOpen}
        initialIndex={galleryIndex}
        onClose={() => setGalleryOpen(false)}
      />
    </>
  );
}
